import { useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { AlertTriangle } from 'lucide-react'

import IconSparkle from 'src/icons/IconSparkle'

import type { CdlAutofillStatus } from '../hooks/useDriverCdlAutofill'

// Same accepted types as the mobile picker input (CdlScanBanner). Drag & drop
// bypasses the input's `accept` filtering, so dropped files are re-checked in
// JS against the same rule and silently ignored when they don't match.
const ACCEPT = 'image/*,application/pdf'

// Files dragged from Finder/Explorer can arrive with an EMPTY `file.type`
// (unregistered MIME, e.g. HEIC on some systems) — fall back to the extension
// before rejecting, so a legitimate photo isn't silently ignored.
const ACCEPTED_EXTENSIONS =
  /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|avif|pdf)$/i
const isAcceptedFile = (file: File) =>
  file.type.startsWith('image/') ||
  file.type === 'application/pdf' ||
  (file.type === '' && ACCEPTED_EXTENSIONS.test(file.name))

// Every rail state is the SAME box: 300px tall, one third of the split's
// width (the parent grid column supplies it — 1fr against the fields' 2fr).
// Otherwise matches the mockup's `.pane`: radius 8px, padding 22px 14px,
// centered column with an 8px gap. The document panes (uploading / review)
// drop the padding so the photo fills the box edge to edge.
const PANE_BASE =
  'flex h-[300px] w-full flex-col items-center justify-center gap-2 rounded-lg px-3.5 py-[22px] text-center'
const PANE_DOC =
  'relative h-[300px] w-full overflow-hidden rounded-lg text-left'

// Card-placeholder pattern for when the driver's file can't be previewed
// (an image the browser can't decode — e.g. HEIC in Chrome).
const DARK_PLACEHOLDER: React.CSSProperties = {
  background:
    'repeating-linear-gradient(135deg, #1b2740 0 12px, #161f33 12px 24px)',
}

interface CdlScanDesktopRailProps {
  status: CdlAutofillStatus
  // True when this scan follows a failed one — the analyzing pane then reads
  // "Re-reading your CDL front…" (mockup Path 2 · 2) instead of "Analyzing
  // document…".
  reReading: boolean
  // Name of the picked file, shown as the caption over the review image.
  fileName: string | null
  // Preview URL for the uploading/review panes: an object URL for images, or
  // a rendered first-page data URL for PDFs. Null while a PDF render is still
  // in flight or failed (placeholder pattern shows). Owned by the parent.
  previewUrl: string | null
  // Called with the picked/dropped file; the parent starts the scan with the
  // 'desktop_upload' route (drag-drop and browse share it). interaction
  // distinguishes the two entry gestures as a scan_started attribute
  // (ENG-4362) without fragmenting the route.
  onFileSelected: (file: File, interaction: 'drag' | 'browse') => void
  // The "Skip — I'll type it in myself" link while analyzing.
  onSkip: () => void
  // RUM returning_applicant dimension (ENG-4362) for the scan_opened emit.
  returningApplicant: boolean
}

/**
 * Desktop (≥ 640px) CDL scan rail for the driver License Information step: a
 * persistent box (one third of the split's width × 300px) left of the fields
 * that morphs through every scan state — dropzone → uploading → analyzing →
 * document image (or an amber failure pane that stays a dropzone). No
 * full-screen takeovers, no source sheet, no camera on desktop; recovery is
 * always dropping/browsing a new file in the same place. The parent owns the
 * autofill orchestration (useDriverCdlAutofill), the prefill/preview, and the
 * green done-banner.
 */
const CdlScanDesktopRail = ({
  status,
  reReading,
  fileName,
  previewUrl,
  onFileSelected,
  onSkip,
  returningApplicant,
}: CdlScanDesktopRailProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  // True when the browser couldn't decode the preview (e.g. HEIC in Chrome) —
  // the placeholder pattern renders instead of a broken image. Reset whenever
  // a new preview URL arrives.
  const [previewFailed, setPreviewFailed] = useState(false)
  useEffect(() => {
    setPreviewFailed(false)
  }, [previewUrl])

  // The document image, filling the whole pane edge to edge: the driver's own
  // upload when it can be shown, otherwise the card-placeholder pattern.
  const docPreviewFill =
    previewUrl && !previewFailed ? (
      <img
        src={previewUrl}
        alt="Your uploaded CDL"
        onError={() => setPreviewFailed(true)}
        className="absolute inset-0 h-full w-full object-cover"
      />
    ) : (
      <div className="absolute inset-0" style={DARK_PLACEHOLDER} />
    )

  // The silent-ignore path, made visible (ENG-4362): rejected drops/picks
  // emit driver_cdl_scan_blocked so Datadog can tell us whether the missing
  // "unsupported file type" error state is a real problem or a non-event.
  // NOT a scan_ended outcome — no scan ever starts, so folding it in would
  // break the one-ended-per-started pairing invariant.
  const trackBlockedFile = (file: File, interaction: 'drag' | 'browse') => {
    datadogRum.addAction('driver_cdl_scan_blocked', {
      component: 'CdlScanDesktopRail',
      route: 'desktop_upload',
      reason: 'invalid_file_type',
      interaction,
      // Empty MIME types fall back to the extension so blocked HEIC-alikes
      // are distinguishable from genuinely unknown files.
      file_type: file.type || file.name.split('.').pop()?.toLowerCase() || '',
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so re-picking the same file still fires onChange.
    e.target.value = ''
    if (!file) return
    // Same gate as the drop path: the `accept` attribute is advisory (OS
    // pickers offer "All Files" / can bypass it), and an off-type file would
    // otherwise start a doomed upload the pipeline can only archive.
    if (isAcceptedFile(file)) onFileSelected(file, 'browse')
    else trackBlockedFile(file, 'browse')
  }

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPT}
      className="hidden"
      onChange={handleFileChange}
    />
  )

  // Shared dropzone behavior for the panes that accept a file (idle + the
  // amber failure panes): whole pane clicks through to the picker, and
  // drag-over swaps to the solid-border highlight.
  // Intent marker (ENG-4362): fires when the driver engages the dropzone —
  // BEFORE any file exists. The gap between scan_opened and scan_started is
  // the drivers who reached the OS picker (or dragged something over) and
  // backed out; without this, they're indistinguishable from drivers who
  // never noticed the feature.
  const trackOpened = (interaction: 'drag' | 'browse') => {
    datadogRum.addAction('driver_cdl_scan_opened', {
      component: 'CdlScanDesktopRail',
      route: 'desktop_upload',
      interaction,
      returning_applicant: returningApplicant,
    })
  }

  const dropzoneProps = {
    type: 'button' as const,
    onClick: () => {
      trackOpened('browse')
      fileInputRef.current?.click()
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      // Only the false→true transition is intent; dragOver fires continuously.
      if (!dragOver) trackOpened('drag')
      setDragOver(true)
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      if (isAcceptedFile(file)) onFileSelected(file, 'drag')
      else trackBlockedFile(file, 'drag')
    },
  }

  if (status === 'uploading') {
    return (
      <div className={`${PANE_DOC} border border-doubleNickel-gray-200`}>
        {docPreviewFill}
        {/* Status overlay along the bottom of the photo. The gradient keeps
            the white text readable over any image (or the placeholder). */}
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 pb-3 pt-8">
          <span className="text-[13px] font-bold text-white">CDL — front</span>
          <span className="text-[11.5px] font-semibold text-white/90">
            Uploading…
          </span>
          {/* Indeterminate: the S3 PUT exposes no progress events, so the bar
              pulses instead of tracking a real percentage. */}
          <div className="h-[5px] w-full overflow-hidden rounded-full bg-white/30">
            <div className="h-full w-3/5 animate-pulse rounded-full bg-doubleNickel-brand-500" />
          </div>
        </div>
      </div>
    )
  }

  if (status === 'analyzing') {
    return (
      <div
        className={`${PANE_BASE} border border-doubleNickel-brand-100 bg-doubleNickel-brand-25`}
      >
        <style>{`@keyframes cdl-sparkle-spin{to{transform:rotate(360deg)}}@keyframes cdl-sparkle-glow{0%,100%{filter:drop-shadow(0 0 3px #1C93D760)}50%{filter:drop-shadow(0 0 10px #1C93D7AA)}}`}</style>
        <span
          className="inline-flex text-doubleNickel-brand-500"
          style={{
            animation:
              'cdl-sparkle-spin 2.5s linear infinite, cdl-sparkle-glow 2s ease-in-out infinite',
          }}
        >
          <IconSparkle size={24} />
        </span>
        <span className="text-[13px] font-semibold text-doubleNickel-brand-700">
          {reReading ? 'Re-reading your CDL front…' : 'Analyzing document…'}
        </span>
        <button
          type="button"
          onClick={onSkip}
          className="text-xs font-semibold text-doubleNickel-brand-600 underline"
        >
          Skip — I&apos;ll type it in myself
        </button>
      </div>
    )
  }

  if (status === 'filled') {
    return (
      <div className={`${PANE_DOC} border border-doubleNickel-gray-200`}>
        {docPreviewFill}
        <span className="absolute left-2.5 top-2.5 rounded-full bg-black/55 px-2.5 py-[3px] text-[10.5px] font-semibold text-white">
          CDL — front
        </span>
        {fileName && (
          <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-7 text-xs font-semibold text-white">
            {fileName}
          </span>
        )}
      </div>
    )
  }

  // Failure panes ('rejected' / 'unreadable' / 'error'): the rail turns amber
  // with the reason and stays a dropzone — recovery is dropping a clearer /
  // different file in the same place. The form stays usable throughout, so no
  // manual-entry link is needed.
  if (status === 'rejected' || status === 'unreadable' || status === 'error') {
    const copy =
      status === 'unreadable'
        ? {
            title: "Let's try that photo again",
            body: "We couldn't read the front of your CDL — it looks blurry or has glare.",
            action: 'Drop a clearer photo, or ',
          }
        : status === 'rejected'
        ? {
            title: "That didn't look like a CDL",
            body: "Make sure it's the front of your commercial driver's license.",
            action: 'Drop another photo, or ',
          }
        : {
            title: "We couldn't read your document",
            body: 'Something went wrong — try again.',
            action: 'Drop your CDL front, or ',
          }
    return (
      <>
        <button
          {...dropzoneProps}
          className={`${PANE_BASE} cursor-pointer border-[1.5px] ${
            dragOver
              ? 'border-solid border-doubleNickel-brand-500 bg-doubleNickel-brand-50'
              : 'border-dashed border-doubleNickel-warning-400 bg-doubleNickel-warning-50'
          }`}
        >
          <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full border border-doubleNickel-warning-200 bg-white text-doubleNickel-warning-600">
            <AlertTriangle size={19} strokeWidth={2} />
          </span>
          <span className="text-[13px] font-bold text-doubleNickel-gray-900">
            {copy.title}
          </span>
          <span className="text-xs leading-normal text-doubleNickel-gray-600">
            {copy.body}
          </span>
          <span className="text-xs leading-normal text-doubleNickel-gray-900">
            {copy.action}
            <u>browse files</u>
          </span>
        </button>
        {hiddenInput}
      </>
    )
  }

  // idle — the dropzone
  return (
    <>
      <button
        {...dropzoneProps}
        className={`${PANE_BASE} cursor-pointer border-[1.5px] ${
          dragOver
            ? 'border-solid border-doubleNickel-brand-500 bg-doubleNickel-brand-50'
            : 'border-dashed border-doubleNickel-brand-200 bg-doubleNickel-brand-25'
        }`}
      >
        <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[10px] border border-doubleNickel-brand-100 bg-white text-doubleNickel-brand-600">
          <IconSparkle size={18} />
        </span>
        <span className="text-[13px] font-bold text-doubleNickel-brand-700">
          Autofill from a photo
        </span>
        <span className="text-xs leading-normal text-doubleNickel-brand-600">
          Drag &amp; drop your CDL front,
          <br />
          or <u>browse files</u>
        </span>
        <span className="text-[11px] text-doubleNickel-gray-500">
          JPG, PNG, HEIC or PDF
        </span>
      </button>
      {hiddenInput}
    </>
  )
}

export default CdlScanDesktopRail
